import { useState, type FormEvent } from 'react';
import { ConfirmDialog } from '@/components/organisms/ConfirmDialog';
import { AlertBanner } from '@/components/molecules/AlertBanner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/format';
import { useValidateCheckoutCodeMutation } from '@/features/billing/queries';
import {
  buildCheckoutSummary,
  checkoutCodeErrorMessage,
} from '@/features/billing/checkout-code-helpers';
import { PromoCodeDisclosure } from '@/features/billing/PromoCodeDisclosure';
import { planDisplayName } from '@/features/billing/plan-helpers';
import {
  localizedPriceFor,
  useDiscountedPricePreview,
  type LocalizedPrice,
} from '@/features/billing/use-price-preview';
import type { BillingPlan, ValidateCheckoutCodeResponse } from '@/lib/billing';

type Props = {
  /** The plan being purchased; `null` closes the dialog. */
  plan: BillingPlan | null;
  /**
   * Paddle's geo-resolved, undiscounted price for this plan — resolved once for
   * the whole grid by `usePricePreview` and threaded down, so the dialog quotes
   * the exact number the card the customer just clicked did. `null` → USD catalog.
   */
  localizedPrice: LocalizedPrice | null;
  /** `paddleCustomerId` from the subscription read; keeps a returning customer's
   * locked currency consistent across the grid and the discounted re-preview. */
  customerId: string | null;
  onClose: () => void;
  /**
   * Proceed to Paddle. `paddleDiscountId` is null unless a code was applied —
   * there is NO auto-apply any more, so a discount reaches Paddle only when the
   * customer typed a code that validated here.
   */
  onProceed: (plan: BillingPlan, paddleDiscountId: string | null) => void;
};

/**
 * Checkout summary with an optional promo code, shown before Paddle's overlay
 * opens. The code field sits behind a "Have a promo code?" link
 * (`PromoCodeDisclosure`) — this dialog's job is to confirm what's being bought,
 * and an open text box above the total reads as a step still to complete.
 *
 * This step exists so spec §7.2's "shown in checkout summary with savings amount"
 * has somewhere to live: previously the checkout CTA jumped straight to Paddle,
 * leaving nowhere to enter a code or preview what it saves.
 *
 * Validation is server-side (`POST /billing/checkout/validate-code`) and
 * READ-ONLY — applying a code here redeems nothing. Paddle counts the redemption
 * when checkout completes; validating first just means the customer learns a code
 * is unusable before they reach the overlay rather than after.
 *
 * Error copy is branched on the 6-digit `errorCode` in `checkoutCodeErrorMessage`,
 * never on the server's message string.
 *
 * Money here is LOCALIZED and per BILLING TERM. Applying a code re-runs
 * `PricePreview` for this one price id WITH the validated `discountId`, so the
 * list price, the discount and the total are three fields of one Paddle response
 * — the same numbers the overlay will show. `validate-code` stays the
 * authorization check; see `buildCheckoutSummary` for the fallback rule.
 */
export function CheckoutDialog({
  plan,
  localizedPrice,
  customerId,
  onClose,
  onProceed,
}: Props) {
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState<ValidateCheckoutCodeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const validate = useValidateCheckoutCodeMutation();
  // Dormant until a code validates — `discountId: null` disables the query.
  const discountPreview = useDiscountedPricePreview({
    priceId: plan?.paddleCheckoutPriceId ?? null,
    customerId,
    discountId: applied?.paddleDiscountId ?? null,
  });

  if (!plan) return null;

  const summary = buildCheckoutSummary({
    plan,
    localizedPrice,
    applied,
    discounted: localizedPriceFor(discountPreview.byPriceId, plan),
  });
  // Formatted once — the breakdown row and the applied-code banner must never
  // be able to quote different savings.
  const savings =
    summary.discount != null ? formatCurrency(summary.discount, summary.currency) : null;
  const trimmed = code.trim();

  function reset(): void {
    setCode('');
    setApplied(null);
    setError(null);
    validate.reset();
  }

  function handleClose(): void {
    reset();
    onClose();
  }

  async function handleApply(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!plan || trimmed === '' || validate.isPending) return;
    setError(null);
    try {
      setApplied(await validate.mutateAsync({ code: trimmed, planId: plan.id }));
    } catch (err) {
      setApplied(null);
      setError(checkoutCodeErrorMessage(err, plan));
    }
  }

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
      title={`Subscribe to ${planDisplayName(plan)}`}
      confirmLabel="Continue to payment"
      cancelLabel="Cancel"
      onConfirm={() => {
        const discountId = applied?.paddleDiscountId ?? null;
        reset();
        onProceed(plan, discountId);
      }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[color:var(--ink-600)]">{planDisplayName(plan)}</span>
            <span className="num text-[color:var(--ink-900)]">
              {summary.listPrice != null
                ? formatCurrency(summary.listPrice, summary.currency)
                : '-'}
            </span>
          </div>
          {applied && savings && summary.total != null && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[color:var(--good-600)]">Discount ({applied.code})</span>
                <span className="num text-[color:var(--good-600)]">−{savings}</span>
              </div>
              <div className="flex items-center justify-between border-t border-[color:var(--ink-200)] pt-1.5 font-medium">
                <span className="text-[color:var(--ink-900)]">Total due today</span>
                <span className="num text-[color:var(--ink-900)]">
                  {formatCurrency(summary.total, summary.currency)}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Both promo states live inside the disclosure so "Remove" hands the
            customer back the open field rather than re-collapsing to the link
            they already answered. A new checkout starts collapsed for free:
            `plan: null` unmounts this subtree along with the disclosure's
            state. */}
        <PromoCodeDisclosure defaultOpen={applied != null || error != null}>
          {({ autoFocus }) =>
            applied ? (
              <AlertBanner variant="success">
                <span className="flex w-full items-center justify-between gap-2">
                  <span>
                    Code {applied.code} applied{savings ? `. You save ${savings}` : ''}.
                  </span>
                  <Button variant="ghost" size="sm" onClick={reset}>
                    Remove
                  </Button>
                </span>
              </AlertBanner>
            ) : (
              <form className="flex items-start gap-2" onSubmit={(e) => void handleApply(e)}>
                <Input
                  autoFocus={autoFocus}
                  aria-label="Promo code"
                  placeholder="Promo code (optional)"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    // Clear a settled error as soon as the field is edited, so stale
                    // copy doesn't sit under a code the user is still typing.
                    if (error) setError(null);
                  }}
                />
                <Button
                  type="submit"
                  variant="outline"
                  disabled={trimmed === '' || validate.isPending}
                >
                  {validate.isPending ? 'Checking…' : 'Apply'}
                </Button>
              </form>
            )
          }
        </PromoCodeDisclosure>

        {error && <AlertBanner variant="error">{error}</AlertBanner>}
      </div>
    </ConfirmDialog>
  );
}
